import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Plan } from 'app/models/plan';
import { PlanPieceService } from 'app/services/PlanPiece/plan-piece.service';
import { AwscontrolService, ImageUploadResponse } from 'app/services/aws/awscontrol.service';
import { PiecesService } from 'app/services/pieces/pieces.service';
import Swal from 'sweetalert2';

type ImageUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-plan-form',
  templateUrl: './plan-form.component.html',
  styleUrls: ['./plan-form.component.scss']
})
export class PlanFormComponent implements OnInit {
  form!: FormGroup;
  pieceId!: number;
  planId?: number;
  isEditMode = false;
  loading = false;
  uploadingImage = false;

  // Image handling
  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;
  currentImageUrl: string | null = null;
  newImageUrl: string | null = null;

  // ==================== MODE + PREVIEW ====================
  imageUploadMode: ImageUploadMode = 'direct';
  previewOriginalUrl: string | null = null;
  previewProcessedUrl: string | null = null;
  isAwaitingValidation: boolean = false;
  // ========================================================

  // Languages
  agencyAvailableLanguages: string[] = ['FR'];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private planService: PlanPieceService,
    private awsService: AwscontrolService,
    private piecesService: PiecesService
  ) {}

  ngOnInit(): void {
    this.pieceId = Number(this.route.snapshot.paramMap.get('pieceId'));
    this.planId = this.route.snapshot.paramMap.get('planId') ? Number(this.route.snapshot.paramMap.get('planId')) : undefined;
    this.isEditMode = !!this.planId;

    this.initForm();

    this.loadPieceLanguages();

    if (this.isEditMode && this.planId) {
      this.loadPlan();
    }
  }

  private loadPieceLanguages(): void {
    if (!this.pieceId) return;
    this.piecesService.getPiece(this.pieceId).subscribe({
      next: (piece: any) => {
        if (piece?.projet?.user?.availableLanguages) {
          this.agencyAvailableLanguages = piece.projet.user.availableLanguages;
        } else {
          this.agencyAvailableLanguages = ['FR'];
        }
      },
      error: () => {
        this.agencyAvailableLanguages = ['FR'];
      }
    });
  }

  initForm(): void {
    this.form = this.fb.group({
      etage: [''],
      sqft: [0],
      description: [''],
      descriptionArab: [''],
      descriptionEnglish: [''],
      nameArab: [''],
      nameEnglish: [''],
      prix: [''],
      videoUrl: ['']
    });
  }

  loadPlan(): void {
    this.loading = true;
    this.planService.getPlan(this.planId!).subscribe({
      next: (plan) => {
        this.form.patchValue({
          etage: plan.etage || '',
          sqft: plan.sqft || 0,
          description: plan.description || '',
          descriptionArab: plan.descriptionArab || '',
          descriptionEnglish: plan.descriptionEnglish || '',
          nameArab: plan.nameArab || '',
          nameEnglish: plan.nameEnglish || '',
          prix: plan.prix || '',
          videoUrl: plan.videoUrl || ''
        });

        if (plan.photosPlan && plan.photosPlan.length > 0) {
          this.currentImageUrl = plan.photosPlan[0];
          this.imagePreview = this.currentImageUrl;
        }

        this.loading = false;
      },
      error: () => {
        Swal.fire('Erreur', 'Impossible de charger le plan', 'error');
        this.loading = false;
      }
    });
  }

  // ==================== HELPERS ====================
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'JPEG, PNG, JPG, GIF, WEBP uniquement', timer: 2000 });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'Max 5MB', timer: 2000 });
      return false;
    }
    return true;
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  // =================================================

  setUploadMode(mode: ImageUploadMode): void {
    if (this.uploadingImage) return;
    if (this.isAwaitingValidation) this.cancelPreview(true);
    this.imageUploadMode = mode;
  }

  // File selection handler
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    if (!this.validateImageFile(file)) {
      input.value = '';
      return;
    }

    this.selectedFile = file;

    // Aperçu local de l'original
    this.fileToDataUrl(file).then(d => {
      this.previewOriginalUrl = d;
      this.imagePreview = d;
    });

    if (this.imageUploadMode === 'direct') {
      // Comportement original : imagePreview local, upload au submit
      this.isAwaitingValidation = false;
      this.previewProcessedUrl = null;
    } else {
      // Imagesousproduit : upload immédiat + validation
      this.uploadViaImagesousproduit(file, input);
    }
  }

  // ==================== NOUVEAU : upload via imagesousproduit ====================
  private uploadViaImagesousproduit(file: File, input: HTMLInputElement): void {
    this.uploadingImage = true;
    this.isAwaitingValidation = false;
    this.previewProcessedUrl = null;

    this.awsService.imagesousproduit([file], 'plans').subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingImage = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.previewProcessedUrl = response.urls[0];
          this.isAwaitingValidation = true;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || 'Échec du traitement',
            confirmButtonColor: '#f44336'
          });
          this.previewOriginalUrl = null;
          this.imagePreview = this.currentImageUrl;
        }
        input.value = '';
      },
      error: () => {
        this.uploadingImage = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter l\'image', confirmButtonColor: '#f44336' });
        this.previewOriginalUrl = null;
        this.imagePreview = this.currentImageUrl;
        input.value = '';
      }
    });
  }

  validatePreview(): void {
    if (!this.previewProcessedUrl) return;
    this.currentImageUrl = this.previewProcessedUrl;
    this.imagePreview = this.previewProcessedUrl;
    this.selectedFile = null;
    this.previewOriginalUrl = null;
    this.previewProcessedUrl = null;
    this.isAwaitingValidation = false;
    Swal.fire({ icon: 'success', title: 'Image validée', timer: 1500, showConfirmButton: false });
  }

  cancelPreview(silent: boolean = false): void {
    const url = this.previewProcessedUrl;
    const doCancel = () => {
      if (url) this.awsService.deleteFile(url).subscribe({ error: () => {} });
      this.previewOriginalUrl = null;
      this.previewProcessedUrl = null;
      this.isAwaitingValidation = false;
      this.selectedFile = null;
      this.imagePreview = this.currentImageUrl;
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser cette image ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }
  // =============================================================================

  clearImage(): void {
    if (this.isAwaitingValidation) { this.cancelPreview(); return; }
    this.selectedFile = null;
    this.imagePreview = this.currentImageUrl;
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  // Upload image to S3 (mode direct)
  uploadImageToS3(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.selectedFile) {
        resolve(this.currentImageUrl || '');
        return;
      }

      this.uploadingImage = true;

      const deleteOldPromise = (this.isEditMode && this.currentImageUrl)
        ? this.awsService.deleteFile(this.currentImageUrl).toPromise().catch(err => {
            console.warn('Could not delete old image:', err);
          })
        : Promise.resolve();

      deleteOldPromise.then(() => {
        this.awsService.uploadFile(this.selectedFile!, 'plans').subscribe({
          next: (response) => {
            this.uploadingImage = false;
            if (response.success && response.urls.length > 0) {
              resolve(response.urls[0]);
            } else {
              reject('Échec de l\'upload');
            }
          },
          error: (err) => {
            this.uploadingImage = false;
            reject(err);
          }
        });
      }).catch(err => {
        this.uploadingImage = false;
        reject(err);
      });
    });
  }

  async onSubmit(): Promise<void> {
    if (this.isAwaitingValidation) {
      Swal.fire({ icon: 'warning', title: 'Validation requise', text: 'Veuillez valider ou refuser l\'image.', confirmButtonColor: '#ff9800' });
      return;
    }

    if (this.form.invalid) {
      return;
    }

    this.loading = true;

    try {
      let finalImageUrl = this.currentImageUrl;

      // Mode direct : on upload au submit si un fichier a été sélectionné
      if (this.imageUploadMode === 'direct' && this.selectedFile) {
        finalImageUrl = await this.uploadImageToS3();
      }
      // Mode imagesousproduit : currentImageUrl est déjà mis à jour après validation

      const formValue = this.form.value;
      const planData: Plan = {
        etage: formValue.etage,
        sqft: formValue.sqft,
        description: formValue.description,
        descriptionArab: formValue.descriptionArab,
        descriptionEnglish: formValue.descriptionEnglish,
        nameArab: formValue.nameArab,
        nameEnglish: formValue.nameEnglish,
        prix: formValue.prix,
        videoUrl: formValue.videoUrl,
        photosPlan: finalImageUrl ? [finalImageUrl] : []
      };

      const request = this.isEditMode && this.planId
        ? this.planService.updatePlan(this.planId, planData)
        : this.planService.createPlan(this.pieceId, planData);

      request.subscribe({
        next: () => {
          Swal.fire('Succès', `Plan ${this.isEditMode ? 'modifié' : 'créé'} avec succès`, 'success');
          this.router.navigate(['/piece', this.pieceId, 'plans']);
        },
        error: (err) => {
          console.error(err);
          Swal.fire('Erreur', 'Opération échouée', 'error');
          this.loading = false;
        }
      });

    } catch (error) {
      console.error(error);
      Swal.fire('Erreur', 'Échec de l\'upload de l\'image', 'error');
      this.loading = false;
    }
  }

  cancel(): void {
    this.router.navigate(['/piece', this.pieceId, 'plans']);
  }
}